import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../api/settings';
import { apiClient } from '../api/client';
import { useTeamMembers, useCreateTeamMember, useUpdateTeamMember, useDeleteTeamMember } from '../hooks/useTeamMembers';
import { TeamMember } from '../types';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)', color: 'var(--text)',
  fontFamily: 'inherit',
};
const textareaStyle: React.CSSProperties = {
  ...inputStyle, resize: 'vertical', minHeight: '120px', lineHeight: 1.6,
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: '5px',
};
const hintStyle: React.CSSProperties = {
  fontSize: '11px', color: 'var(--text3)', marginTop: '4px',
};

function TeamMembersSection() {
  const { data: members = [], isLoading } = useTeamMembers();
  const createMutation = useCreateTeamMember();
  const updateMutation = useUpdateTeamMember();
  const deleteMutation = useDeleteTeamMember();
  const [editing, setEditing] = useState<Partial<TeamMember> | null>(null);
  const [isNew, setIsNew] = useState(false);

  const startNew = () => {
    setEditing({ full_name: '', position: '', phone: '', mobile: '', email: '' });
    setIsNew(true);
  };

  const startEdit = (m: TeamMember) => {
    setEditing({ ...m });
    setIsNew(false);
  };

  const save = async () => {
    if (!editing?.full_name?.trim()) return;
    if (isNew) {
      await createMutation.mutateAsync(editing);
    } else if (editing.id) {
      await updateMutation.mutateAsync({ id: editing.id, payload: editing });
    }
    setEditing(null);
  };

  const remove = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const editSet = (field: string, value: string) =>
    setEditing(prev => prev ? { ...prev, [field]: value } : prev);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Team / Ansprechpartner</span>
        <button type="button" onClick={startNew}
          style={{ padding: '5px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '12px', fontWeight: 500, cursor: 'pointer', marginLeft: 'auto' }}>
          + Neues Teammitglied
        </button>
      </div>
      <div className="card-body">
        <div style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '14px' }}>
          Diese Personen können als Ansprechpartner auf Investment-Teasern angezeigt werden.
        </div>

        {isLoading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
        ) : members.length === 0 && !editing ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>
            Noch keine Team-Mitglieder angelegt.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {members.map(m => (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                background: 'var(--surface2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500 }}>{m.full_name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text3)' }}>
                    {[m.position, m.email, m.phone].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button type="button" onClick={() => startEdit(m)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--text3)', padding: '4px 8px' }}>
                  Bearbeiten
                </button>
                <button type="button" onClick={() => remove(m.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--accent)', padding: '4px 8px' }}>
                  Löschen
                </button>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div style={{ marginTop: '14px', padding: '16px', background: 'var(--surface2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '12px', color: 'var(--accent)' }}>
              {isNew ? 'Neues Teammitglied' : 'Teammitglied bearbeiten'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={labelStyle}>Name *</label>
                <input style={inputStyle} value={editing.full_name ?? ''}
                  onChange={e => editSet('full_name', e.target.value)} placeholder="Jana Rubin" autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Position</label>
                <input style={inputStyle} value={editing.position ?? ''}
                  onChange={e => editSet('position', e.target.value)} placeholder="Managing Director" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div>
                <label style={labelStyle}>Telefon</label>
                <input style={inputStyle} value={editing.phone ?? ''}
                  onChange={e => editSet('phone', e.target.value)} placeholder="+49 69 ..." />
              </div>
              <div>
                <label style={labelStyle}>Mobil</label>
                <input style={inputStyle} value={editing.mobile ?? ''}
                  onChange={e => editSet('mobile', e.target.value)} placeholder="+49 176 ..." />
              </div>
              <div>
                <label style={labelStyle}>E-Mail</label>
                <input style={inputStyle} value={editing.email ?? ''}
                  onChange={e => editSet('email', e.target.value)} placeholder="name@rubin.estate" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setEditing(null)}
                style={{ padding: '6px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '12px', cursor: 'pointer' }}>
                Abbrechen
              </button>
              <button type="button" onClick={save}
                style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}>
                Speichern
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LogoUpload({ currentUrl, onUploaded }: { currentUrl?: string; onUploaded: (url: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const displayUrl = previewUrl || (currentUrl?.startsWith('/api') ? currentUrl : currentUrl ? `/api/v1/settings/logo` : null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiClient.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `/api/v1/settings/logo?t=${Date.now()}`;
      setPreviewUrl(url);
      onUploaded(url);
    } catch {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
      {displayUrl && (
        <div style={{
          width: '80px', height: '80px', borderRadius: 'var(--radius)',
          border: '1px solid var(--border)', overflow: 'hidden', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
        }}>
          <img src={displayUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      )}
      <div>
        <label style={{
          padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)',
          borderRadius: 'var(--radius)', fontSize: '13px', cursor: uploading ? 'not-allowed' : 'pointer',
          display: 'inline-block', color: 'var(--text2)',
        }}>
          {uploading ? 'Wird hochgeladen...' : displayUrl ? 'Logo ändern' : 'Logo hochladen'}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" hidden
            onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); }} />
        </label>
        <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '6px' }}>
          JPEG, PNG, WebP oder SVG. Wird auf Investment-Teasern und E-Mails verwendet.
        </div>
        {currentUrl && !currentUrl.startsWith('/api') && (
          <div style={{ marginTop: '4px' }}>
            <input style={{ ...inputStyle, fontSize: '11px', color: 'var(--text3)' }} value={currentUrl} readOnly />
          </div>
        )}
      </div>
    </div>
  );
}

export function CompanySettingsPage() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSettings()
      .then(s => setSettings(s))
      .catch(() => setError('Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: string, value: string) =>
    setSettings(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError(null);
    try {
      await updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header-title">Unternehmen</div>
          <div className="page-header-sub">Stammdaten, Team & rechtliche Hinweise</div>
        </div>
      </div>
      <div className="content">
        <div style={{ maxWidth: '800px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '9px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Wird gespeichert...' : saved ? 'Gespeichert' : 'Speichern'}
            </button>
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Branding */}
            <div className="card">
              <div className="card-header"><span className="card-title">Firmenangaben</span></div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>Firmenname</label>
                    <input style={inputStyle} value={settings.company_name ?? ''} onChange={e => set('company_name', e.target.value)} placeholder="Rubin Real Estate" />
                  </div>
                  <div>
                    <label style={labelStyle}>Juristischer Name</label>
                    <input style={inputStyle} value={settings.company_legal_name ?? ''} onChange={e => set('company_legal_name', e.target.value)} placeholder="BAJK Capital GmbH" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>Straße</label>
                    <input style={inputStyle} value={settings.company_street ?? ''} onChange={e => set('company_street', e.target.value)} placeholder="Hauptstr. 25" />
                  </div>
                  <div>
                    <label style={labelStyle}>PLZ</label>
                    <input style={inputStyle} value={settings.company_zip ?? ''} onChange={e => set('company_zip', e.target.value)} placeholder="65760" />
                  </div>
                  <div>
                    <label style={labelStyle}>Stadt</label>
                    <input style={inputStyle} value={settings.company_city ?? ''} onChange={e => set('company_city', e.target.value)} placeholder="Eschborn" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>Website</label>
                    <input style={inputStyle} value={settings.company_website ?? ''} onChange={e => set('company_website', e.target.value)} placeholder="www.rubin.estate" />
                  </div>
                  <div>
                    <label style={labelStyle}>Telefon</label>
                    <input style={inputStyle} value={settings.company_phone ?? ''} onChange={e => set('company_phone', e.target.value)} placeholder="+49 69 175361653" />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Firmenlogo</label>
                  <LogoUpload
                    currentUrl={settings.company_logo_url}
                    onUploaded={(url) => set('company_logo_url', url)}
                  />
                </div>
              </div>
            </div>

            {/* Team */}
            <TeamMembersSection />

            {/* Widerrufsbelehrung */}
            <div className="card">
              <div className="card-header"><span className="card-title">Widerrufsbelehrung / Rechtliche Hinweise</span></div>
              <div className="card-body">
                <label style={labelStyle}>Text (wird unter jede E-Mail angehängt und auf Teasern verwendet)</label>
                <textarea style={{ ...textareaStyle, minHeight: '160px' }}
                  value={settings.widerrufsbelehrung ?? ''}
                  onChange={e => set('widerrufsbelehrung', e.target.value)}
                  placeholder="Diese E-Mail und ihre Anhänge sind vertraulich..." rows={8} />
                <div style={hintStyle}>Leer lassen, um keinen rechtlichen Hinweis anzufügen.</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '10px 28px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Wird gespeichert...' : saved ? 'Gespeichert' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
